import { useMemo } from 'react'
import { useSettings } from 'app/hooks/useSettings'
import type { StageType } from 'app/types/entities'

export type ConfiguredStage = StageType & { visible: boolean; system?: boolean | null }

type StageVisibility = Record<string, { visible?: boolean; order?: number }>

type VisibilityList = {
  id?: string | null
  stages?: readonly { name: string; color?: string; order?: number | null; system?: boolean | null }[] | null
}

/**
 * Per-user stage visibility and order for one list, read and written under the same setting
 * key the old pipelines page used so nobody's hidden stages reappear. The board draws from
 * it; the list's settings dialog edits it. A built-in (system) stage is always visible, whatever
 * was saved before it became unhideable.
 */
export function useStageVisibility(pipeline: VisibilityList): {
  configuredStages: ConfiguredStage[]
  visibleStages: ConfiguredStage[]
  saveVisibility: (nextStages: ConfiguredStage[]) => Promise<void>
} {
  const key = `columnVisibility-/pipelines${pipeline.id}`
  const [visibilitySettings, saveVisibilitySettings] = useSettings<StageVisibility>(key)
  const configuredStages: ConfiguredStage[] = useMemo(
    () =>
      (pipeline.stages ?? []).map((stage, index) => {
        const stageSettings = visibilitySettings?.[stage.name]
        return {
          ...stage,
          visible: stage.system || (stageSettings?.visible ?? true),
          order: stageSettings?.order ?? stage.order ?? index + 1,
        }
      }),
    [pipeline.stages, visibilitySettings],
  )
  const visibleStages = useMemo(
    () => configuredStages.filter((stage) => stage.visible).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [configuredStages],
  )
  const saveVisibility = async (nextStages: ConfiguredStage[]) => {
    const stageSettings = nextStages.reduce<StageVisibility>(
      (acc, stage) => ({ ...acc, [stage.name]: { visible: stage.visible, order: stage.order } }),
      {},
    )
    await saveVisibilitySettings(key, stageSettings)
  }
  return { configuredStages, visibleStages, saveVisibility }
}
